/**
 * Web File destination — the redesigned transfer list.
 *
 * Mirror of the extension's `sidepanel/components/FileDestination.tsx`. The two
 * differ only in their bindings: web reads everything from one
 * `useFileTransfers` hook (no SW↔page message layer to split it across) and
 * takes its labels from the typed `messages` catalog rather than
 * `browser.i18n`.
 *
 * SECURITY: never logs file names or content (security.md). The filename is
 * rendered and nothing else.
 */
import { FT_MAX_BYTES, type SendProgressEvent, sendFile } from "@reflecto/client-core";
import {
  type FileSendProgressEvent,
  type FileTransferCardLabels,
  type FileTransferItem,
  FileTransferList,
  type FileTransferListLabels,
  type FileTransferState,
  formatBytes,
  type SendCardState,
  Text,
  useFileSend,
} from "@reflecto/client-ui";
import { useCallback, useMemo, useState } from "react";
import { withSenderAnalytics } from "../filetransfer/ftAnalytics";
import { cancelActiveSend, createWebSenderDeps } from "../filetransfer/senderTransfer.web";
import type { useFileTransfers } from "../filetransfer/useFileTransfers";
import { detectLocale, messages } from "../i18n";
import { ftErrorMessage } from "./ftErrorMessages";

const CARD_LABELS: FileTransferCardLabels = {
  preparing: messages.fileTransfer.sendPreparing,
  waitingApproval: messages.fileTransfer.sendWaitingAccept,
  waitingApprovalAppOpen: messages.fileTransfer.sendWaitingAppOpen,
  waitingApprovalAppOpenHint: messages.fileTransfer.sendWaitingAppOpenHint,
  transferring: messages.fileTransfer.transferringPct,
  done: messages.fileTransfer.sendDone,
  cancelled: messages.fileTransfer.receiveCancelled,
  expired: messages.fileTransfer.errorExpired,
  accept: messages.fileTransfer.receiveAccept,
  reject: messages.fileTransfer.receiveDecline,
  cancel: messages.fileTransfer.sendCancel,
  dismiss: messages.fileTransfer.dismiss,
  transportDirect: messages.fileTransfer.transportDirect,
  transportDirectHint: messages.fileTransfer.transportDirectHint,
  transportRelay: messages.fileTransfer.transportRelay,
  transportRelayHint: messages.fileTransfer.transportRelayHint,
  progress: messages.fileTransfer.progressLabel,
};

const LIST_LABELS: FileTransferListLabels = {
  title: messages.nav.files,
  chooseFile: messages.fileTransfer.sendChooseFile,
  outgoing: messages.fileTransfer.outgoing,
  incoming: messages.fileTransfer.incoming,
  acceptAll: messages.fileTransfer.acceptAll,
  dropZone: {
    title: messages.fileTransfer.dropTitle,
    // Derived from the real ceiling, never a hardcoded number: the limit comes
    // from the protocol's part-count bound and the server can tighten it
    // further, so a figure typed into 28 catalogs would go stale.
    limit: messages.fileTransfer.dropLimit(formatBytes(FT_MAX_BYTES, detectLocale())),
    ariaLabel: messages.fileTransfer.dropAriaLabel,
  },
  card: CARD_LABELS,
  // A finished INBOUND transfer is "Saved.", not "Sent!" — the shared bag above
  // is written from the sender's side. Only `done` differs.
  incomingCard: { ...CARD_LABELS, done: messages.fileTransfer.receiveDone },
};

/** Falls back to the closed `SendFailReason` set; the raw code is never shown. */
function sendFailText(reason: string): string {
  switch (reason) {
    case "rejected":
      return messages.fileTransfer.sendFailedRejected;
    case "no-answer":
      return messages.fileTransfer.sendFailedNoAnswer;
    case "upload-failed":
      return messages.fileTransfer.sendFailedUploadFailed;
    case "cancelled":
      return messages.fileTransfer.sendFailedCancelled;
    default:
      return messages.fileTransfer.sendFailedGeneric;
  }
}

/**
 * Map the send machine onto a card state. `delivering` and `waiting-accept`
 * both read as "waiting for approval" — the difference is whether the phone
 * has acked the offer, which is engine detail the user cannot act on.
 */
function toCardState(state: SendCardState): FileTransferState | null {
  switch (state.status) {
    case "idle":
    case "too-large":
      return null;
    case "preparing":
      return { kind: "preparing", pct: state.pct };
    case "delivering":
      return { kind: "waiting-approval" };
    // The ack is engine detail the user can't act on, so it stays flattened above.
    // Whether the phone must be picked up is NOT — it's the one thing here they can act on.
    case "waiting-accept":
      return { kind: "waiting-approval", needsAppOpen: state.needsAppOpen };
    case "uploading":
      return {
        kind: "transferring",
        // Parts are ~5 MiB, so this is coarse but honest.
        pct: state.partCount > 0 ? Math.floor((state.partIndex / state.partCount) * 100) : 0,
      };
    case "done":
      return { kind: "done" };
    case "failed":
      return {
        kind: "failed",
        message: ftErrorMessage(state.errorCode) ?? sendFailText(state.reason),
      };
  }
}

export interface FileDestinationProps {
  transfers: ReturnType<typeof useFileTransfers>;
}

export function FileDestination({ transfers }: FileDestinationProps) {
  const { devices, offers, progress, outcome, onAccept, onDecline, onCancelReceive } = transfers;
  const targetId = devices[0]?.id;
  // The picked file is held only so the row can name it — the transfer starts
  // immediately, since the redesign has no separate Send button.
  const [pending, setPending] = useState<{ name: string; size: number } | null>(null);
  const [tooLarge, setTooLarge] = useState(false);
  // The transfers hook never clears `outcome`, so without this one completed
  // transfer would hold a row for the rest of the session and the drop zone
  // could never come back.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

  const capabilities = useMemo(
    () => ({
      maxBytes: FT_MAX_BYTES,
      async send(
        file: File,
        recipientDeviceId: string,
        onProgress: (event: FileSendProgressEvent) => void
      ): Promise<void> {
        // Analytics taps at the call site that owns onProgress, never inside
        // createWebSenderDeps, which receives the same callback and would
        // compose into a double capture. `file` is the only place the size is
        // known. Web is relay-only, so no demote wrapper.
        const tapped = withSenderAnalytics({ resumed: false, sizeBytes: file.size }, onProgress);
        const deps = await createWebSenderDeps(recipientDeviceId, tapped);
        await sendFile(file, recipientDeviceId, deps);
      },
      cancel: (transferId: string, onProgress?: (event: SendProgressEvent) => void) =>
        cancelActiveSend(transferId, withSenderAnalytics({ resumed: false }, onProgress)),
    }),
    []
  );
  const { state, peerChunksDone, busy, send, cancel, reset } = useFileSend(capabilities);

  const handleFileSelected = useCallback(
    (file: File) => {
      if (file.size > capabilities.maxBytes) {
        setTooLarge(true);
        setPending(null);
        return;
      }
      setTooLarge(false);
      setPending({ name: file.name, size: file.size });
      if (targetId) send(file, targetId);
    },
    [targetId, capabilities.maxBytes, send]
  );

  const outgoing: FileTransferItem[] = useMemo(() => {
    const cardState = toCardState(state);
    if (!cardState || !pending) return [];
    const terminal =
      cardState.kind === "done" || cardState.kind === "failed" || cardState.kind === "expired";
    return [
      {
        id: "outgoing",
        fileName: pending.name,
        // Same rule as the receive row below — see the extension's
        // FileDestination.tsx for the full reasoning. A relayed SEND used to
        // show no chip at all for the whole upload; silence is reserved for
        // "route not settled yet", which is only true before `uploading`.
        transport:
          state.status === "uploading"
            ? state.transport === "p2p"
              ? "direct"
              : "relay"
            : undefined,
        fileSizeLabel: formatBytes(pending.size, detectLocale()),
        state: cardState,
        // A finished row's × clears it. A live one cancels — but ONLY once the
        // upload loop is running: during `preparing` the transferId is still
        // undefined, and during delivering/waiting-accept no cancellable record
        // exists yet (pre-P-S1), so a × there would be a dead button that
        // appears to withdraw an offer the phone still receives.
        onCancel: terminal
          ? () => {
              reset();
              setPending(null);
            }
          : cardState.kind === "transferring"
            ? cancel
            : undefined,
      },
    ];
  }, [state, pending, cancel, reset]);

  const incoming: FileTransferItem[] = useMemo(() => {
    const rows: FileTransferItem[] = offers.map((offer) => ({
      id: offer.transferId,
      fileName: offer.fileName,
      fileSizeLabel: formatBytes(offer.fileSize, detectLocale()),
      state: { kind: "offered" as const },
      onAccept: () => onAccept(offer.transferId),
      onReject: () => onDecline(offer.transferId),
    }));

    if (progress && !dismissed.has(progress.transferId)) {
      rows.unshift({
        id: progress.transferId,
        fileName: progress.fileName,
        // Carried over from the accepted offer. Blank only when the tab was
        // reloaded mid-transfer, which loses the name too.
        fileSizeLabel:
          progress.fileSize != null ? formatBytes(progress.fileSize, detectLocale()) : "",
        state: {
          kind: "transferring",
          pct:
            progress.partCount > 0
              ? Math.floor((progress.partIndex / progress.partCount) * 100)
              : 0,
        },
        // The wire only marks p2p; anything else IS the relay, so name it
        // rather than leaving the chip blank — silence would be ambiguous
        // between "relayed" and "route not settled yet".
        transport: progress.transport === "p2p" ? "direct" : "relay",
        onCancel: () => onCancelReceive(progress.transferId),
      });
    }
    // NOT `else if`: the hook holds one progress slot and one outcome slot, so
    // a completed-but-undismissed row would vanish the moment the user accepted
    // the next offer — and then be lost for good when the single outcome slot
    // is overwritten. The pre-redesign ReceiveFileCard rendered these as two
    // independent blocks; this restores that.
    if (
      outcome &&
      outcome.transferId !== progress?.transferId &&
      !dismissed.has(outcome.transferId)
    ) {
      rows.unshift({
        id: outcome.transferId,
        fileName: outcome.fileName,
        fileSizeLabel:
          outcome.fileSize != null ? formatBytes(outcome.fileSize, detectLocale()) : "",
        state:
          outcome.outcome === "complete"
            ? { kind: "done" }
            : outcome.outcome === "cancelled"
              ? { kind: "cancelled" }
              : {
                  kind: "failed",
                  message: ftErrorMessage(outcome.errorCode) ?? messages.fileTransfer.receiveFailed,
                },
        onCancel: () => setDismissed((prev) => new Set(prev).add(outcome.transferId)),
      });
    }
    return rows;
  }, [offers, progress, outcome, dismissed, onAccept, onDecline, onCancelReceive]);

  return (
    <FileTransferList
      outgoing={outgoing}
      incoming={incoming}
      onFileSelected={handleFileSelected}
      labels={LIST_LABELS}
      // No paired phone, or a send already running — either way there is
      // nothing useful a second pick could do.
      pickerDisabled={!targetId || busy}
      onAcceptAll={() => {
        for (const offer of offers) onAccept(offer.transferId);
      }}
      footnote={
        busy ? (
          // Below the rows, not above: it describes a transfer you can already
          // see. Closing the panel mid-send aborts the upload; the resume
          // trigger only recovers it on the NEXT open.
          <Text size="xs" tone="tertiary" role="status">
            {peerChunksDone === null
              ? messages.fileTransfer.sendKeepOpenHint
              : messages.fileTransfer.sendPeerProgress(peerChunksDone)}
          </Text>
        ) : undefined
      }
      banner={
        tooLarge ? (
          <Text size="xs" tone="negative" role="status">
            {messages.fileTransfer.sendTooLarge(Math.floor(FT_MAX_BYTES / (1024 * 1024)))}
          </Text>
        ) : !targetId ? (
          <Text size="xs" tone="tertiary" role="status">
            {messages.fileTransfer.sendNoDevice}
          </Text>
        ) : undefined
      }
    />
  );
}
